import type { ReactNode } from 'react';
import { api, type ApiError, type EventDTO, type PurgeSelection } from './api';
import type { PurgeEntity, PurgePreviewDTO, PurgeOperationDTO, SuperAdminCompanyDTO } from '@wberp/shared';
import { useEffect, useRef, useState } from 'react';

const statusNames: Record<string, string> = {
  queued: '排队中', running: '执行中', processing: '处理中', waiting: '等待中', paused: '已暂停', unsellable: '1688 链接不符合要求',
  completed: '已完成', succeeded: '成功', ready: '预处理完成', failed: '失败', cancelled: '已取消',
  blocked: '受阻', deleted: '回收站', draft: '草稿', valid: '校验通过', invalid: '校验失败',
};
const levelNames: Record<string, string> = { debug: '调试', info: '信息', warn: '警告', error: '错误' };
const businessStageNames: Record<string, string> = {
  completed: '已完成', failed: '失败', cancelled: '已取消', paused: '已暂停', blocked: '受阻', unsellable: '1688 链接不符合要求',
  queued: '等待调度', retry_ready: '等待重试', repair_requested: '等待修复',
  image_apparel_check: '校验搜图商品服装模式', image_assess: '判断主图是否需要清理', image_prepare: 'AI 清理搜图主图', image_search: '搜索 1688 候选', image_rerank: '候选相似度重排',
  collect: '采集商品资料', product_suitability: '判断整商品跨境适售性', candidates: '筛选颜色与尺码', sellability: '判断可售规格',
  multipack: '判断多件装', packaging: '估算包装参数', image_plan: '规划商品图片', images: '生成并上传图片', persist: '保存商品资料',
  content: '准备发布内容', cards: '创建并确认商品卡', media: '上传商品图片', inventory: '设置店铺库存',
  planned: '待提交平台', submitted: '已提交平台', submit_unknown: '提交结果待确认', submit_failed: '提交失败',
  card_confirmation_pending: '等待平台确认商品卡', card_confirmed: '商品卡已确认',
  card_create_rejected: '平台拒绝创建商品卡', card_retry_planned: '等待重试创建', simulated: '模拟完成',
};
const waitReasonNames: Record<string, string> = {
  step_complete: '当前步骤完成，等待继续', listing_lock: '等待店铺发布任务完成',
  wb_listing_card_plan_wait: '等待商品卡计划准备完成', wb_batch_ready: '等待商品卡批次处理',
  image_retry: '等待图片重试', image_capacity: '等待图片处理资源',
  ai_config_changed: 'AI 配置已更新，等待继续处理',
  publish_subject_generation: '等待复用商品类目结果', publish_content_generation: '等待复用商品发布内容',
  PUBLISH_GENERATION_LEASE_LOST: '商品内容准备中断，等待恢复',
  image_recognition_capacity: '等待图片相似度识别资源',
  image_preparation_capacity: '等待搜图主图清理资源',
  worker_stopping: '工作进程正在停止，任务稍后继续', execution_interrupted: '任务中断，等待恢复',
  WB_CARD_TOTAL_QUOTA_WAIT: '等待店铺剩余额度', WB_CARD_QUOTA_WAIT: '等待明日发品额度',
  WB_CARD_CONFIRMATION_TIMEOUT: '卡片确认超过8次，需人工核对',
  WB_CARD_CONFIRMATION_PENDING: '等待 WB 确认商品卡',
  LISTING_LEASE_WAIT: '等待店铺发布锁释放', WB_MEDIA_WAIT: '等待 WB 图片保存重试',
  WB_INVENTORY_WAIT: '等待 WB 库存更新重试',
  shop_binding_archived: '旧绑定已封存，等待恢复检查',
  WB_NETWORK_WAIT: '等待 WB 网络恢复后自动继续',
  AI_SERVICE_WAIT: '等待 AI 服务恢复',
  AI_PROVIDER_LIMIT_WAIT: '等待 AI 服务商额度恢复',
};
const businessEventNames: Record<string, string> = {
  'product.purged': '商品已彻底删除', 'product.import.accepted': '商品导入已接收',
  'product.import.item_rejected': '商品导入失败', 'product.restored': '商品已恢复',
  'product.trashed': '商品已移入回收站', 'product.version.created': '商品资料版本已创建',
  'publish.batch.created': '发布批次已创建', 'publish.target.repair_requested': '已请求修复发布目标',
  'worker.capacity.snapshot': '工作进程容量记录', 'worker.execution.error': '任务执行异常',
  'worker.started': '工作进程已启动', 'workflow.step.completed': '业务步骤已完成',
};
const serviceNames: Record<string, string> = {
  api: '接口服务', 'preprocess-worker': '预处理服务', 'publish-worker': '发布服务', 'purge-worker': '清理服务',
};

export function labelBusinessStage(value: string | null | undefined): string {
  if (!value) return '—';
  return businessStageNames[value] ?? '处理中';
}

export function labelWaitReason(value: string | null | undefined): string {
  if (!value) return '—';
  return waitReasonNames[value] ?? value;
}

export function labelBusinessEvent(value: string): string {
  return businessEventNames[value] ?? value;
}

export function labelService(value: string): string {
  return serviceNames[value] ?? value;
}

export function labelEnvironment(value: string): string {
  return value === 'real' ? '正式模式' : value === 'simulation' ? '模拟模式' : value;
}

export function labelStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return statusNames[status] ?? status;
}

export function statusTone(status: string | null | undefined): 'success' | 'waiting' | 'danger' | 'blue' | 'warning' | 'muted' {
  if (!status) return 'muted';
  if (['completed', 'succeeded', 'ready', 'valid'].includes(status)) return 'success';
  if (['waiting', 'queued', 'paused', 'blocked', 'draft'].includes(status)) return 'waiting';
  if (status === 'unsellable') return 'warning';
  if (['failed', 'cancelled', 'invalid'].includes(status)) return 'danger';
  if (['running', 'processing'].includes(status)) return 'blue';
  return 'muted';
}

export function Pill({ status, children }: { status?: string | null; children?: ReactNode }) {
  const tone = statusTone(status);
  return <span className={`pill pill-${tone}`}>{children ?? labelStatus(status)}</span>;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function Loading({ label = '正在读取服务端数据…' }: { label?: string }) {
  return <div className="loading"><span className="spinner" aria-hidden="true" />{label}</div>;
}

export function Empty({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="empty"><div><div className="empty-icon">⌕</div><strong>{title}</strong>{description && <div>{description}</div>}{action && <div style={{ marginTop: 13 }}>{action}</div>}</div></div>;
}

export function ProductIdentity({ title, sourceProductId, thumbnailUrl, productId, sourcePlatform = '1688', badge, onClick }: {
  title?: string | null;
  sourceProductId?: string | null;
  thumbnailUrl?: string | null;
  productId?: string | null;
  sourcePlatform?: string;
  badge?: ReactNode;
  onClick?: () => void;
}) {
  const label = title || (sourceProductId ? `来源商品 ${sourceProductId}` : productId ? `商品 ${productId}` : '未命名商品');
  const image = thumbnailUrl?.startsWith('https://') ? thumbnailUrl : null;
  const sourceLabel = sourcePlatform === 'wildberries' ? 'WB' : sourcePlatform;
  const content = <><span className="product-thumbnail"><span aria-hidden="true">{sourceLabel}</span>{image && <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(event) => event.currentTarget.remove()} />}</span><span className="product-identity-copy"><span className="cell-title" title={label}>{label}</span>{(sourceProductId || badge) && <span className="product-identity-meta">{sourceProductId && <span className="cell-sub">{sourceLabel} · {sourceProductId}</span>}{badge}</span>}</span></>;
  return onClick
    ? <button type="button" className="product-identity product-identity-button" aria-label={`查看商品：${label}`} onClick={onClick}>{content}</button>
    : <div className="product-identity">{content}</div>;
}

export function ErrorBox({ error, onRetry }: { error: Error | null; onRetry?: () => void }) {
  if (!error) return null;
  const apiError = error as ApiError;
  const errorId = apiError.errorId;
  return (
    <div className="error-box" role="alert" aria-live="assertive">
      <span aria-hidden="true">!</span>
      <div className="error-copy">
        <div>{error.message || '请求失败'}</div>
        {errorId && <div className="error-id">问题编号：{errorId}</div>}
      </div>
      {errorId && <CopyButton value={errorId} label="复制编号" />}
      {onRetry && <button className="button button-small" onClick={onRetry}>重试</button>}
    </div>
  );
}

export function CopyButton({ value, label = '复制' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }
  return <button type="button" className="copy-button" onClick={() => void copy()} aria-label={`${label} ${value}`}>{copied ? '已复制' : label}</button>;
}

export function PageHead({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return <div className="page-head"><div><h1 className="page-title">{title}</h1>{description && <p className="page-description">{description}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</div>;
}

export function Pagination({
  cursor, nextCursor, pageNumber, onNext, onPrevious,
}: { cursor?: string; nextCursor?: string | null; pageNumber: number; onNext: () => void; onPrevious: () => void }) {
  return <div className="pagination"><span>第 {pageNumber} 页 · 每页最多 30 条</span><div className="pagination-actions"><button className="button button-small" disabled={!cursor} onClick={onPrevious}>上一页</button><button className="button button-small" disabled={!nextCursor} onClick={onNext}>下一页</button></div></div>;
}

export function EventTimeline({ events }: { events: EventDTO[] }) {
  if (!events.length) return <Empty title="暂无事件" description="当前范围内还没有可显示的事件。" />;
  return (
    <div className="timeline">
      {events.map((event) => (
        <div className="timeline-item" key={event.eventId}>
          <div className="timeline-rail"><span className="timeline-dot" /></div>
          <div className="timeline-content">
            <div className="timeline-title"><span title={event.eventName}>{labelBusinessEvent(event.eventName)}</span><time className="timeline-time">{formatDate(event.occurredAt)}</time></div>
            <div className="timeline-meta">
              {event.service && <span>{labelService(event.service)}</span>}
              {event.step && <span title={event.step}>阶段：{labelBusinessStage(event.step)}</span>}
              {event.level && <span>级别：{levelNames[event.level] ?? event.level}</span>}
              {event.errorId && <span className="timeline-error">问题编号 {event.errorId}<CopyButton value={event.errorId} /></span>}
              {event.traceId && <span className="mono">链路 {event.traceId}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Modal({ title, description, onClose, children, actions, wide = false, size = 'default', className = '' }: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  size?: 'default' | 'large';
  className?: string;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`modal${wide ? ' modal-wide' : ''}${size === 'large' ? ' modal-large' : ''}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-head"><div><h2>{title}</h2>{description && <p>{description}</p>}</div><button className="button button-icon button-quiet" onClick={onClose} aria-label="关闭">×</button></header>
        <div className="modal-body">{children}</div>
        {actions && <footer className="modal-foot">{actions}</footer>}
      </section>
    </div>
  );
}

export function PurgeDialog({ entity, selection, onClose, onDone }: { entity: PurgeEntity; selection: PurgeSelection; onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<PurgePreviewDTO | null>(null);
  const [operation, setOperation] = useState<PurgeOperationDTO | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const notifiedOperation = useRef<string | null>(null);
  const selectionKey = JSON.stringify(selection);
  useEffect(() => {
    if (operation) return;
    let active = true;
    setBusy(true);
    api.previewPurge(entity, selection).then((value) => { if (active) setPreview(value); }).catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason : new Error('读取删除预览失败')); }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [entity, selectionKey, operation]);
  useEffect(() => {
    if (!operation || ['completed', 'partial', 'failed'].includes(operation.status)) return;
    const timer = window.setInterval(() => { api.getPurgeOperation(operation.id).then(setOperation).catch((reason: unknown) => setError(reason instanceof Error ? reason : new Error('读取删除进度失败'))); }, 1000);
    return () => window.clearInterval(timer);
  }, [operation]);
  useEffect(() => {
    if (!operation || !['completed', 'partial', 'failed'].includes(operation.status) || notifiedOperation.current === operation.id) return;
    notifiedOperation.current = operation.id;
    onDone();
  }, [operation, onDone]);
  async function confirm(): Promise<void> {
    if (!preview || !window.confirm(`将彻底删除 ${preview.selected} 条记录，且不可恢复。不会删除 WB 远端商品，已发出的请求无法撤回。${preview.unknownExternalOperations ? ` 当前存在 ${preview.unknownExternalOperations} 个未知外部操作。` : ''}继续？`)) return;
    setBusy(true); setError(null);
    try { setOperation(await api.purge(entity, selection, preview.selectionHash)); } catch (reason) { setError(reason instanceof Error ? reason : new Error('提交彻底删除失败')); } finally { setBusy(false); }
  }
  const finished = operation && ['completed', 'partial', 'failed'].includes(operation.status);
  return <Modal title="彻底删除确认" description="只清理 ERP 本地数据；不会删除 WB 远端商品，已发出的外部请求无法撤回。" onClose={onClose} actions={<><button className="button" onClick={onClose}>{finished ? '关闭' : '取消'}</button>{!operation && <button className="button button-danger" disabled={busy || !preview || preview.selected === 0} onClick={() => void confirm()}>{busy ? '读取中…' : '确认彻底删除'}</button>}</>}>
    {error && <ErrorBox error={error} />}
    {!preview && !error && <Loading label="正在生成删除预览…" />}
    {preview && !operation && <div className="stack"><div className="notice">匹配 {preview.total}，可删除 {preview.selected}；运行中 {preview.running}，等待中 {preview.waiting}。</div><div className="field-help">关联商品 {preview.relatedProducts} 件 · 关联任务 {preview.relatedJobs} 个 · 图片 {preview.relatedImages} 张 · 资源 {preview.relatedAssets} 个{preview.skippedJobs ? `；跳过 ${preview.skippedJobs} 个没有可删除关联商品的任务` : ''}</div><div className="grid grid-3"><div className="card card-pad"><div className="metric-label">关联目标</div><div className="metric-value">{preview.relatedTargets}</div></div><div className="card card-pad"><div className="metric-label">关联批次</div><div className="metric-value">{preview.relatedBatches}</div></div><div className="card card-pad"><div className="metric-label">未知外部操作</div><div className="metric-value">{preview.unknownExternalOperations}</div></div></div><div className="field-help">{selection.source === 'preprocess_jobs' ? '将永久删除关联商品及其全部任务和关联发布记录。' : ''}删除不可恢复。运行中任务会先失效本地租约，外部请求可能继续完成。</div></div>}
    {operation && <div className="stack"><div className="notice">操作 {operation.status === 'completed' ? '已完成' : operation.status === 'partial' ? '部分完成' : operation.status === 'failed' ? '失败' : '执行中'}：{operation.processed} / {operation.total}</div><div className="field-help">已删除 {operation.deleted}，失败 {operation.failed}{operation.message ? `；${operation.message}` : ''}</div></div>}
  </Modal>;
}

export function JsonPretty({ value }: { value: unknown }) {
  return <pre className="json-preview">{JSON.stringify(value, null, 2)}</pre>;
}

export function ApiStatus({ available, blockers }: { available: boolean; blockers?: string[] }) {
  return <div><div className="status-line"><span className={`status-dot ${available ? 'online' : 'offline'}`} />{available ? '身份服务可用' : '身份服务不可用'}</div>{blockers && blockers.length > 0 && <div className="field-help" style={{ marginTop: 7 }}>{blockers.join('；')}</div>}</div>;
}

export function GlobalCompanyFilter({ value, onChange }: { value: string; onChange: (companyId: string) => void }) {
  const [companies, setCompanies] = useState<SuperAdminCompanyDTO[]>([]);
  useEffect(() => {
    let active = true;
    void api.getSuperAdminCompanies().then((result) => { if (active) setCompanies(result.items); }).catch(() => { if (active) setCompanies([]); });
    return () => { active = false; };
  }, []);
  return <label className="field global-company-filter"><span>公司</span><select className="select" value={value} onChange={(event) => onChange(event.target.value)}><option value="">全部公司</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.name}{company.status === 'disabled' ? '（已停用）' : ''}</option>)}</select></label>;
}
